import { useQueryClient } from "@tanstack/react-query"
import { log } from "helpers/logger"
import { useInfiniteGetQuery } from "hooks/queries"
import PropTypes from "prop-types"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useAccountAlias } from "resources/account"
import { RefreshableError } from "../helpers/errors"
import { useAiTeammateContext, useThreadsContext } from "../hooks/context"
import useSendMessage from "../hooks/send-message"
import useStreamingSendMessage from "../hooks/send-message-streaming"
import { buildMessageList, findLastAssistantMessageId } from "../utils/message-list"
import { MessagesContext, StreamingContext } from "./messages-context"

const MESSAGES_PER_PAGE = 50
const EMPTY_STREAMING_PARTS = []
const EMPTY_STREAMING_TEXT = ""
const EMPTY_STEPS_BY_MESSAGE_ID = {}
const NO_FINISHED_TURN_MESSAGE_ID = null
const NO_STOP_SEND_MESSAGE = null

export function MessagesProvider({ children, initialPrompt }) {
  const userMessageInputOverrideCallbacks = useRef(new Map())
  const userMessageInputFocusCallbacks = useRef(new Map())
  const pendingInputOverrideRef = useRef(null)
  const pendingInputFocusRef = useRef(false)
  const consumedInitialPromptRef = useRef(null)
  const accountAlias = useAccountAlias()
  const { setError, isOpen: isDrawerOpen, isManagedAgent } = useAiTeammateContext()
  const { threadId, newThread, threads, isThreadsLoaded, isPreferencesLoaded: isThreadsPreferencesLoaded } = useThreadsContext()
  const blockingSendMessage = useSendMessage()
  const streamingSendMessage = useStreamingSendMessage()
  const { stop: streamingStop } = streamingSendMessage
  const { mutate: sendMessage, isError: isSendError, isPending: isSendingMessage, variables: sendMessageVariables } = isManagedAgent ? streamingSendMessage : blockingSendMessage
  const queryClient = useQueryClient()

  const queryKey = useMemo(() => ["ai-teammate-messages", accountAlias, threadId], [accountAlias, threadId])

  const hasCachedData = !!queryClient.getQueryData(queryKey)

  const { isFetching, isFetchingNextPage, data, hasNextPage, refetch, fetchNextPage, error: fetchError } = useInfiniteGetQuery({
    url: `/api/${accountAlias}/ai/chat/threads/${threadId}?limit=${MESSAGES_PER_PAGE}`,
    key: queryKey,
    enabled: !!threadId && !hasCachedData,
    staleTime: Infinity,
    getNextPageParam: (lastPage) => lastPage.nextMessageId,
    initialPageParam: undefined,
  })

  const messages = useMemo(() => buildMessageList(data), [data])
  const lastAssistantMessageId = useMemo(() => findLastAssistantMessageId(messages), [messages])
  const hasMessages = useMemo(() => messages.length > 0, [messages])
  // Stay quiet only for a thread deleted elsewhere: once the list has loaded without it, ThreadsProvider
  // drops the active thread, so its 404 is expected. While the list is still loading (or failed to load)
  // a genuine fetch error must still surface — don't gate suppression on the list being present.
  const threadDeleted = useMemo(
    () => isThreadsLoaded && threadId != null && !threads.some((thread) => thread.threadId === threadId),
    [isThreadsLoaded, threads, threadId],
  )

  useEffect(() => {
    if (fetchError && !threadDeleted) {
      log(fetchError)
      setError(new RefreshableError("An unexpected error occurred while fetching the messages. Please try refreshing the thread or resetting it."))
    }
  }, [fetchError, threadDeleted, setError])

  const sendMessageWrapper = useCallback((messageText) => {
    sendMessage({ messageText, accountAlias, threadId, lastAssistantMessageId })
  }, [sendMessage, accountAlias, threadId, lastAssistantMessageId])

  const registerUserMessageInputOverrideCallback = useCallback((key, callback) => {
    userMessageInputOverrideCallbacks.current.set(key, callback)
    // Apply any override queued before this input mounted.
    if (pendingInputOverrideRef.current != null) {
      const { text, sourceMeta } = pendingInputOverrideRef.current
      callback(text, sourceMeta)
      pendingInputOverrideRef.current = null
    }
    return () => userMessageInputOverrideCallbacks.current.delete(key)
  }, [])

  const dispatchUserMessageInputOverride = useCallback((userMessageInputOverride, sourceMeta) => {
    const callbacks = userMessageInputOverrideCallbacks.current
    if (callbacks.size === 0) {
      // No input mounted yet — queue the override for the next registration.
      pendingInputOverrideRef.current = { text: userMessageInputOverride, sourceMeta }
      return
    }
    callbacks.forEach(callback => callback(userMessageInputOverride, sourceMeta))
  }, [])

  const registerUserMessageInputFocusCallback = useCallback((key, callback) => {
    userMessageInputFocusCallbacks.current.set(key, callback)
    // Honor a focus request issued before the input mounted (see focusUserMessageInput).
    if (pendingInputFocusRef.current) {
      callback()
      pendingInputFocusRef.current = false
    }
    return () => userMessageInputFocusCallbacks.current.delete(key)
  }, [])

  const focusUserMessageInput = useCallback(() => {
    const callbacks = userMessageInputFocusCallbacks.current
    if (callbacks.size === 0) {
      // No input mounted yet — focus on the next registration.
      pendingInputFocusRef.current = true
      return
    }
    callbacks.forEach(callback => callback())
  }, [])

  // Nudge initialPrompt: open a fresh thread and pre-fill once per prompt.
  useEffect(() => {
    // setPreferences overwrites threadId if it arrives after newThread().
    if (!isDrawerOpen || !initialPrompt || !isThreadsPreferencesLoaded || consumedInitialPromptRef.current === initialPrompt) {
      if (!initialPrompt) {
        // Drawer closed before input mounted — discard any queued prefill from the abandoned prompt.
        pendingInputOverrideRef.current = null
        pendingInputFocusRef.current = false
      }
      return
    }
    consumedInitialPromptRef.current = initialPrompt
    // Prime pending queues for when the input isn't mounted yet.
    pendingInputOverrideRef.current = { text: initialPrompt, sourceMeta: { source: "nudge" } }
    pendingInputFocusRef.current = true
    // Always a fresh thread — never prefill into a restored one.
    newThread()
    // Direct-deliver if already mounted — the input lives in AiTeammateThread which isn't keyed on threadId, so newThread() never causes a remount.
    const overrideCbs = userMessageInputOverrideCallbacks.current
    if (overrideCbs.size > 0) {
      overrideCbs.forEach(cb => cb(initialPrompt, { source: "nudge" }))
      pendingInputOverrideRef.current = null
    }
    const focusCbs = userMessageInputFocusCallbacks.current
    if (focusCbs.size > 0) {
      focusCbs.forEach(cb => cb())
      pendingInputFocusRef.current = false
    }
  }, [isDrawerOpen, initialPrompt, newThread, isThreadsPreferencesLoaded])

  const value = useMemo(() => ({
    registerUserMessageInputOverrideCallback,
    registerUserMessageInputFocusCallback,
    dispatchUserMessageInputOverride,
    focusUserMessageInput,
    fetchNextPage,
    messages,
    hasMessages,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isSendingMessage,
    sendMessage: sendMessageWrapper,
    lastAssistantMessageId,
    refetch,
    // Expose the last user message text from the failed mutation (for retry functionality)
    lastUserMessageText: isSendError ? sendMessageVariables?.messageText : null,
    stopSendMessage: isManagedAgent ? streamingStop : NO_STOP_SEND_MESSAGE,
  }), [
    registerUserMessageInputOverrideCallback,
    registerUserMessageInputFocusCallback,
    dispatchUserMessageInputOverride,
    focusUserMessageInput,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    messages,
    hasMessages,
    sendMessageWrapper,
    lastAssistantMessageId,
    isSendError,
    sendMessageVariables,
    isSendingMessage,
    refetch,
    isManagedAgent,
    streamingStop,
  ])

  const streamingValue = useMemo(() => ({
    streamingParts: isManagedAgent ? streamingSendMessage.streamingParts : EMPTY_STREAMING_PARTS,
    streamingText: isManagedAgent ? streamingSendMessage.streamingText : EMPTY_STREAMING_TEXT,
    stepsByMessageId: isManagedAgent ? streamingSendMessage.stepsByMessageId : EMPTY_STEPS_BY_MESSAGE_ID,
    finishedTurnMessageId: isManagedAgent ? streamingSendMessage.finishedTurnMessageId : NO_FINISHED_TURN_MESSAGE_ID,
  }), [isManagedAgent, streamingSendMessage.streamingParts, streamingSendMessage.streamingText, streamingSendMessage.stepsByMessageId, streamingSendMessage.finishedTurnMessageId])

  return <MessagesContext.Provider value={value}>
    <StreamingContext.Provider value={streamingValue}>{children}</StreamingContext.Provider>
  </MessagesContext.Provider>
}

MessagesProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialPrompt: PropTypes.string,
}
